import { useEffect, useRef, useState } from 'react';
import BuildingView from './BuildingView';
import useViews from '../hooks/useViews';

import logo from '../assets/ps_symbol.svg';

import PintarStudioLogo from './PintarStudioLogo';
import { currentSlideAtom, infoTooltipOpenAtom } from '../states/planner';
import { useAtom } from 'jotai';

import { IframeButton } from './IframeButton';
import { PanoramaButton } from './PanoramaButton';
import { MapButton } from './MapButton';
import useSite from '../hooks/useSite';
import { ApartmentView } from './ApartmentView';
import { FilterOptions } from './FilterOptions';
import { AreaViewHover } from './AreaViewHover';
import { AreaApartmentHover } from './AreaApartmentHover';
import { AreaInfoHover } from './AreaInfoHover';

import { CompassButton } from './CompassButton';
import { CompassOptions } from './CompassOptions';
import { FirstTimePopup } from './FirstTimePopup';
import { PrevAndNextButtons } from './PrevAndNextButtons';

const SWIPE_THRESHOLD = 50; // px to trigger a slide change

const CarouselBuildingViews = () => {
  const [site] = useSite();
  const [siteViews] = useViews();

  const [aspect, setAspect] = useState([0, 0]);
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useAtom(currentSlideAtom);
  const [infoTooltipOpen, setInfoTooltipOpen] = useAtom(infoTooltipOpenAtom);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  useEffect(() => {
    if (siteViews.length > 0) {
      const img = new Image();
      img.onload = () => {
        setAspect([img.width, img.height]);
        setLoaded(true);
      };
      img.src = siteViews[0].daypicture;
    }
  }, [siteViews]);

  if (!loaded) {
    return (
      <section className="sec-loading">
        <div className="one">
          <img className="bottom-10 h-32 w-32" alt="logo" src={logo}></img>
        </div>
      </section>
    );
  }

  const totalSlides = siteViews.length;

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) > SWIPE_THRESHOLD) {
      if (distance > 0 && currentSlide < totalSlides - 1) {
        // swipe left → next slide
        setCurrentSlide(currentSlide + 1);
      } else if (distance < 0 && currentSlide > 0) {
        // swipe right → prev slide
        setCurrentSlide(currentSlide - 1);
      }
    }
  };

  return (
    <div className="relative select-none pb-12">
      {/* Frame that masks the slides */}
      <div
        className="overflow-hidden"
        style={{ aspectRatio: `${aspect[0]} / ${aspect[1]}` }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {siteViews.map((view, index) => {
            let svgurl = view.svgurl;
            if (svgurl) {
              svgurl =
                process.env.NODE_ENV === 'development'
                  ? view.svgurl.replace(
                      'https://firebasestorage.googleapis.com/',
                      'http://localhost:3008/firebasestorage/'
                    )
                  : view.svgurl;
            }

            return (
              <div key={index} className="relative w-full flex-shrink-0 overflow-hidden" style={{ flexBasis: '100%' }}>
                <BuildingView viewid={view.viewid} picture={view.daypicture} compass={view.compass} svgurl={svgurl} />
                <AreaViewHover />
                <AreaApartmentHover />
                <AreaInfoHover />
              </div>
            );
          })}
        </div>
      </div>

      {/* Overlay to close tooltip */}
      <div
        className={`absolute left-0 top-0 h-full w-full cursor-default ${infoTooltipOpen ? 'visible' : 'invisible'}`}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setInfoTooltipOpen(false)}
        onClick={() => setInfoTooltipOpen(false)}
      ></div>

      <FilterOptions />
      <CompassOptions
        siteViews={siteViews}
        overviewPicture={site.overviewpicture}
        overviewPictureSVG={site.overviewpicturesvg}
      />

      {/* Bottom buttons row */}
      <div className="absolute bottom-14 left-1 flex space-x-[2px] md:left-2 md:space-x-[6px]">
        <IframeButton />
        <MapButton />
        <PanoramaButton />
      </div>

      <PintarStudioLogo />
      <FirstTimePopup />

      {/* Compass (half-overlapping the image frame) */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <CompassButton siteViews={siteViews} overviewPicture={site.overviewpicture} />
      </div>

      <ApartmentView />

      <PrevAndNextButtons totalSlides={totalSlides} />
    </div>
  );
};

export default CarouselBuildingViews;
